import React, { useState } from 'react';
import {
    Box,
    Button,
    Drawer,
    IconButton,
    Stack,
    Typography,
    Chip,
    Paper,
    Divider,
    alpha,
    useTheme,
    Tooltip,
    TextField,
} from '@mui/material';
import { FilterList, Close, Refresh, Download } from '@mui/icons-material';

import ReportTypeSelector from './ReportTypeSelector';
import ReportFilters from './ReportFilters';

const ReportToolbar = ({
    reportType,
    onReportTypeChange,
    filters,
    onFilterChange,
    onGenerate,
    loading,
    businesses,
    floorplans,
    onDownloadPDF,
    showDeferredCost = true,
}) => {
    const theme = useTheme();
    const [drawerOpen, setDrawerOpen] = useState(false);

    // Calculate active filter count (excluding date and report type)
    // For Company Performance, exclude all filters
    // For Deferred Cost, exclude status and cost filters
    const getActiveFilterCount = () => {
        let count = 0;
        if (reportType === 'COMPANY_PERFORMANCE') {
            // Company Performance report has no applicable filters
            return 0;
        }
        if (reportType === 'DEFERRED_COST') {
            // Deferred Cost report excludes status and cost filters
            if (filters.assignedToBusinessIds?.length > 0) count++;
            if (filters.floorplanIds?.length > 0) count++;
            return count;
        }
        if (filters.statuses?.length > 0) count++;
        if (filters.assignedToBusinessIds?.length > 0) count++;
        if (filters.floorplanIds?.length > 0) count++;
        if (filters.hasDeferredCost !== 'ALL') count++;
        return count;
    };

    const activeFilterCount = getActiveFilterCount();

    const handleDateChange = (field, value) => {
        onFilterChange({
            ...filters,
            [field]: value || null,
        });
    };

    const handleClearAllFilters = () => {
        onFilterChange({
            ...filters,
            statuses: [],
            assignedToBusinessIds: [],
            floorplanIds: [],
            hasDeferredCost: 'ALL',
            includeInternalOnly: true,
            onlyInternallyCreated: false,
        });
    };

    return (
        <Paper
            elevation={0}
            sx={{
                p: { xs: 2, sm: 2.5 },
                mb: 3,
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: 3,
                bgcolor: 'background.paper',
            }}
        >
            {/* Main Controls Row */}
            <Stack
                direction={{ xs: 'column', md: 'row' }}
                spacing={2}
                alignItems={{ xs: 'stretch', md: 'center' }}
                justifyContent="space-between"
            >
                {/* Left: Report Type & Date */}
                <Stack
                    direction={{ xs: 'column', sm: 'row' }}
                    spacing={2}
                    alignItems={{ xs: 'stretch', sm: 'center' }}
                    sx={{ flex: 1 }}
                >
                    <Box sx={{ minWidth: { xs: '100%', sm: 220 } }}>
                        <ReportTypeSelector
                            value={reportType}
                            onChange={onReportTypeChange}
                            showDeferredCost={showDeferredCost}
                        />
                    </Box>

                    {/* Hide date pickers for snapshot reports */}
                    {reportType !== 'PERIOD_ACTIVITY' && reportType !== 'DEFERRED_COST' && (
                        <>
                            <Divider
                                orientation="vertical"
                                flexItem
                                sx={{ display: { xs: 'none', sm: 'block' } }}
                            />

                            <Stack
                                direction={{ xs: 'column', sm: 'row' }}
                                spacing={1}
                                alignItems="center"
                                sx={{ flex: 1, width: { xs: '100%', sm: 'auto' } }}
                            >
                                <TextField
                                    label="From"
                                    type="date"
                                    size="small"
                                    value={filters.dateFrom || ''}
                                    onChange={(e) => handleDateChange('dateFrom', e.target.value)}
                                    InputLabelProps={{
                                        shrink: true,
                                    }}
                                    sx={{
                                        minWidth: 140,
                                        width: { xs: '100%', sm: 'auto' },
                                    }}
                                />
                                <Typography
                                    variant="body2"
                                    color="text.secondary"
                                    sx={{ px: 0.5, display: { xs: 'none', sm: 'block' } }}
                                >
                                    →
                                </Typography>
                                <TextField
                                    label="To"
                                    type="date"
                                    size="small"
                                    value={filters.dateTo || ''}
                                    onChange={(e) => handleDateChange('dateTo', e.target.value)}
                                    InputLabelProps={{
                                        shrink: true,
                                    }}
                                    sx={{
                                        minWidth: 140,
                                        width: { xs: '100%', sm: 'auto' },
                                    }}
                                />
                            </Stack>
                        </>
                    )}
                </Stack>

                {/* Right: Actions */}
                <Stack direction="row" spacing={1.5}>
                    {/* Hide filter button for Company Performance - no applicable filters */}
                    {reportType !== 'COMPANY_PERFORMANCE' && (
                        <Button
                            variant="outlined"
                            size="medium"
                            startIcon={<FilterList />}
                            onClick={() => setDrawerOpen(true)}
                            sx={{
                                fontWeight: 500,
                                borderRadius: 2,
                                textTransform: 'none',
                                borderColor: activeFilterCount > 0 ? 'primary.main' : 'divider',
                                color: activeFilterCount > 0 ? 'primary.main' : 'text.secondary',
                            }}
                        >
                            Filters
                            {activeFilterCount > 0 && (
                                <Chip
                                    label={activeFilterCount}
                                    size="small"
                                    color="primary"
                                    sx={{ ml: 1, height: 20, fontSize: '0.7rem' }}
                                />
                            )}
                        </Button>
                    )}
                    <Button
                        variant="contained"
                        size="medium"
                        startIcon={<Refresh />}
                        onClick={onGenerate}
                        disabled={loading}
                        disableElevation
                        sx={{
                            fontWeight: 600,
                            borderRadius: 2,
                            textTransform: 'none',
                            px: 3,
                            bgcolor: 'primary.main',
                            '&:hover': {
                                bgcolor: 'primary.dark',
                            },
                        }}
                    >
                        {loading ? 'Loading...' : 'Generate'}
                    </Button>
                    {onDownloadPDF && (
                        <Tooltip title="Download PDF">
                            <IconButton
                                onClick={onDownloadPDF}
                                disabled={loading}
                                sx={{
                                    border: '1px solid',
                                    borderColor: 'divider',
                                    borderRadius: 2,
                                    color: 'text.secondary',
                                    '&:hover': {
                                        borderColor: 'primary.main',
                                        color: 'primary.main',
                                        bgcolor: alpha(theme.palette.primary.main, 0.04),
                                    },
                                }}
                            >
                                <Download />
                            </IconButton>
                        </Tooltip>
                    )}
                </Stack>
            </Stack>

            {/* Active Filters Display */}
            {activeFilterCount > 0 && (
                <Box sx={{ mt: 2, pt: 2, borderTop: `1px solid ${theme.palette.divider}` }}>
                    <Stack
                        direction="row"
                        spacing={1}
                        flexWrap="wrap"
                        alignItems="center"
                        useFlexGap
                    >
                        <Typography
                            variant="caption"
                            sx={{
                                fontWeight: 600,
                                color: 'text.secondary',
                                textTransform: 'uppercase',
                                letterSpacing: '0.5px',
                                mr: 1,
                            }}
                        >
                            Filtered by:
                        </Typography>

                        {/* Status Chips - Hidden for Deferred Cost */}
                        {reportType !== 'DEFERRED_COST' &&
                            filters.statuses?.map((status) => (
                                <Chip
                                    key={status}
                                    label={status}
                                    size="small"
                                    onDelete={() =>
                                        onFilterChange({
                                            ...filters,
                                            statuses: filters.statuses.filter((s) => s !== status),
                                        })
                                    }
                                    sx={{
                                        bgcolor: alpha(theme.palette.primary.main, 0.08),
                                        borderRadius: 1.5,
                                    }}
                                />
                            ))}

                        {/* Company Chips */}
                        {filters.assignedToBusinessIds?.map((id) => {
                            const business = businesses.find((b) => b.businessId === id);
                            return (
                                <Chip
                                    key={id}
                                    label={business?.businessName || 'Company'}
                                    size="small"
                                    onDelete={() =>
                                        onFilterChange({
                                            ...filters,
                                            assignedToBusinessIds:
                                                filters.assignedToBusinessIds.filter(
                                                    (bid) => bid !== id
                                                ),
                                        })
                                    }
                                    sx={{
                                        bgcolor: alpha(theme.palette.info.main, 0.08),
                                        borderRadius: 1.5,
                                    }}
                                />
                            );
                        })}

                        {/* Floorplan Chips */}
                        {filters.floorplanIds?.map((id) => {
                            const floorplan = floorplans.find((f) => f.floorplanId === id);
                            return (
                                <Chip
                                    key={id}
                                    label={floorplan?.name || 'Floorplan'}
                                    size="small"
                                    onDelete={() =>
                                        onFilterChange({
                                            ...filters,
                                            floorplanIds: filters.floorplanIds.filter(
                                                (fid) => fid !== id
                                            ),
                                        })
                                    }
                                    sx={{
                                        bgcolor: alpha(theme.palette.success.main, 0.08),
                                        borderRadius: 1.5,
                                    }}
                                />
                            );
                        })}

                        {/* Cost Filter Chip - Hidden for Deferred Cost */}
                        {reportType !== 'DEFERRED_COST' && filters.hasDeferredCost !== 'ALL' && (
                            <Chip
                                label={
                                    filters.hasDeferredCost === 'WITH_COST' ? 'Has Cost' : 'No Cost'
                                }
                                size="small"
                                onDelete={() =>
                                    onFilterChange({ ...filters, hasDeferredCost: 'ALL' })
                                }
                                sx={{
                                    bgcolor: alpha(theme.palette.warning.main, 0.08),
                                    borderRadius: 1.5,
                                }}
                            />
                        )}

                        {/* Clear All */}
                        <Button
                            size="small"
                            onClick={handleClearAllFilters}
                            sx={{
                                ml: 1,
                                textTransform: 'none',
                                fontSize: '0.75rem',
                                color: 'text.secondary',
                                '&:hover': {
                                    color: 'error.main',
                                    bgcolor: alpha(theme.palette.error.main, 0.08),
                                },
                            }}
                        >
                            Clear all
                        </Button>
                    </Stack>
                </Box>
            )}

            {/* Filter Drawer - Hidden for Company Performance (no applicable filters) */}
            {reportType !== 'COMPANY_PERFORMANCE' && (
                <Drawer
                    anchor="right"
                    open={drawerOpen}
                    onClose={() => setDrawerOpen(false)}
                    PaperProps={{
                        sx: {
                            width: { xs: '100%', sm: 380 },
                            p: 0,
                            borderTopLeftRadius: 16,
                            borderBottomLeftRadius: 16,
                        },
                    }}
                >
                    {/* Drawer Header */}
                    <Box
                        sx={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            p: 2.5,
                            borderBottom: `1px solid ${theme.palette.divider}`,
                        }}
                    >
                        <Typography variant="h6" fontWeight={600}>
                            Filters
                        </Typography>
                        <IconButton
                            onClick={() => setDrawerOpen(false)}
                            size="small"
                            sx={{
                                bgcolor: alpha(theme.palette.text.primary, 0.05),
                                '&:hover': {
                                    bgcolor: alpha(theme.palette.text.primary, 0.1),
                                },
                            }}
                        >
                            <Close fontSize="small" />
                        </IconButton>
                    </Box>

                    {/* Drawer Content */}
                    <Box sx={{ p: 2.5, flex: 1, overflowY: 'auto' }}>
                        <ReportFilters
                            filters={filters}
                            onChange={onFilterChange}
                            businesses={businesses}
                            floorplans={floorplans}
                            reportType={reportType}
                            onGenerate={() => {
                                onGenerate();
                                setDrawerOpen(false);
                            }}
                            loading={loading}
                            showDeferredCost={showDeferredCost}
                        />
                    </Box>
                </Drawer>
            )}
        </Paper>
    );
};

export default ReportToolbar;
